/**
 * useCustomerCms — fetches the customer-app CMS config once per session and
 * exposes O(1) helpers to check page/feature visibility and pull ordering.
 *
 *   const cms = useCustomerCms();
 *   cms.isPageVisible("hygiene");
 *   cms.isFeatureVisible("hygiene", "bag_new");
 *   cms.orderPages(BOTTOM_TABS, (t) => t.key);
 */
import { useEffect, useState } from "react";
import { custApi } from "./customerApi";

let _cache = null;
let _pending = null;

const empty = { pages: {}, features: {}, unavailable_overlays: {}, ready: false };

async function _fetch() {
  if (_cache) return _cache;
  if (_pending) return _pending;
  _pending = custApi.get("/me/cms/customer-app")
    .then(r => {
      const pages = {};
      (r.data?.pages || []).forEach((p, i) => {
        pages[p.key] = { visible: true, order: p.order ?? i };
      });
      const features = {};
      Object.entries(r.data?.features || {}).forEach(([pk, arr]) => {
        features[pk] = {};
        arr.forEach((f, i) => {
          features[pk][f.key] = { visible: true, order: f.order ?? i };
        });
      });
      const unavailable_overlays = r.data?.unavailable_overlays || {};
      _cache = { pages, features, unavailable_overlays, ready: true };
      return _cache;
    })
    .catch(() => {
      // Fail-open — if the CMS endpoint is unreachable, show everything.
      _cache = { pages: null, features: null, unavailable_overlays: {}, ready: true };
      return _cache;
    });
  return _pending;
}

export function refreshCustomerCms() {
  _cache = null;
  _pending = null;
}

export function useCustomerCms() {
  const [state, setState] = useState(_cache || empty);
  useEffect(() => {
    if (_cache) { setState(_cache); return; }
    let alive = true;
    _fetch().then(v => { if (alive) setState(v); });
    return () => { alive = false; };
  }, []);

  const isPageVisible = (key) => {
    if (!state.ready) return true;
    if (state.pages == null) return true;  // fail-open
    return !!state.pages[key];
  };
  const isFeatureVisible = (pageKey, featureKey) => {
    if (!state.ready) return true;
    if (state.features == null) return true;
    const page = state.features[pageKey];
    if (!page) return true;
    return !!page[featureKey];
  };
  const orderPages = (items, getKey) => {
    if (!state.ready || state.pages == null) return items;
    return [...items].sort((a, b) => {
      const ao = state.pages[getKey(a)]?.order ?? 999;
      const bo = state.pages[getKey(b)]?.order ?? 999;
      return ao - bo;
    });
  };
  const orderFeatures = (pageKey, items, getKey) => {
    if (!state.ready || state.features == null) return items;
    const page = state.features[pageKey];
    if (!page) return items;
    return [...items].sort((a, b) => {
      const ao = page[getKey(a)]?.order ?? 999;
      const bo = page[getKey(b)]?.order ?? 999;
      return ao - bo;
    });
  };
  const filterFeatures = (pageKey, items, getKey) => {
    if (!state.ready || state.features == null) return items;
    return items.filter(i => isFeatureVisible(pageKey, getKey(i)));
  };

  return { ...state, isPageVisible, isFeatureVisible, orderPages, orderFeatures, filterFeatures };
}
